import { Button, Card, CardContent, CardHeader, CardTitle } from '@folio/components';
import { useToast } from '../../components/ui/use-toast';
import { useQuery } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import {
  legacyPermissionsControllerAtMeQueryOptions,
  viewSpecificControllerListChildBrokerageFirmsQueryOptions,
  viewSpecificControllerListWholesaleBrokerageFirmDistributionRequestsQueryOptions,
} from 'korint-sdk';
import { useKorintConfig } from 'korint-sdk/react';
import { useTranslation } from 'react-i18next';

export function IntermediaryGeneralModule() {
  const { t } = useTranslation();
  const toast = useToast();
  const { korintConfig } = useKorintConfig();
  const config = korintConfig.modulesConfig?.intermediaryGeneralModule;

  const { data: user, status: userStatus } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { data: totalChildBrokerageFirmsCount, status: totalChildBrokerageFirmsCountStatus } =
    useQuery({
      ...viewSpecificControllerListChildBrokerageFirmsQueryOptions(user?.brokerageFirmId!, {
        page: 1,
        count: 1,
        hasSomeDistributionRequestWithStatus: [
          'APPROVED',
          'PENDING',
          'WAITING_FOR_SIGNATURE',
          'DENIED',
          'REVOKED',
        ],
      }),
      enabled: !!user?.brokerageFirmId,
      select: data => data.totalCount,
    });

  const { data: pendingDistributionRequestsCount, status: pendingDistributionRequestsStatus } =
    useQuery({
      ...viewSpecificControllerListWholesaleBrokerageFirmDistributionRequestsQueryOptions(
        user?.brokerageFirmId!,
        {
          statuses: ['PENDING'],
          page: 1,
          count: 1,
        },
      ),
      enabled: !!user?.brokerageFirmId,
      select: data => data.totalCount,
    });

  const { data: childBrokerageFirmsCount, status: childBrokerageFirmsStatus } = useQuery({
    ...viewSpecificControllerListChildBrokerageFirmsQueryOptions(user?.brokerageFirmId!, {
      page: 1,
      count: 1,
      hasSomeDistributionRequestWithStatus: ['APPROVED'],
    }),
    enabled: !!user?.brokerageFirmId,
    select: data => data.totalCount,
  });

  const isLoading =
    userStatus === 'pending' ||
    pendingDistributionRequestsStatus === 'pending' ||
    totalChildBrokerageFirmsCountStatus === 'pending' ||
    childBrokerageFirmsStatus === 'pending';

  const hasError =
    userStatus === 'error' ||
    pendingDistributionRequestsStatus === 'error' ||
    totalChildBrokerageFirmsCountStatus === 'error' ||
    childBrokerageFirmsStatus === 'error';

  const handleCopyLink = async () => {
    if (!user?.brokerageFirmId) {
      return;
    }

    const url = `${window.location.origin}/${user.brokerageFirmId}/brokerage-firm`;

    try {
      await navigator.clipboard.writeText(url);
      toast.toast({
        title: t('intermediaryGeneral.toast.linkCopiedTitle'),
        description: t('intermediaryGeneral.toast.linkCopiedDescription'),
      });
    } catch (error) {
      toast.toast({
        title: t('intermediaryGeneral.toast.errorTitle'),
        description: t('intermediaryGeneral.toast.errorDescription'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-4">
          <span>{t('intermediaryGeneral.overviewTitle')}</span>
          {config?.displayInscriptionLink && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleCopyLink}
              disabled={!user?.brokerageFirmId}
              leftIcon={<Copy className="h-4 w-4" />}
            >
              {t('intermediaryGeneral.copyLink')}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4 animate-pulse">
            {[...Array(3)].map((_, idx) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: is ok
              <div key={idx} className="rounded-md p-4 bg-gray-10 h-[80px]" />
            ))}
          </div>
        ) : hasError ? (
          <div className="text-center text-muted-foreground">
            {t('intermediaryGeneral.errorMessage')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
            <div className="rounded-md p-4 bg-gray-10">
              <div className="text-sm text-gray-500">
                {t('intermediaryGeneral.stats.totalRequests')}
              </div>
              <div className="text-2xl font-semibold">{totalChildBrokerageFirmsCount}</div>
            </div>
            <div className="rounded-md p-4 bg-gray-10">
              <div className="text-sm text-gray-500">
                {t('intermediaryGeneral.stats.actionRequired')}
              </div>
              <div className="text-2xl font-semibold">{pendingDistributionRequestsCount}</div>
            </div>
            <div className="rounded-md p-4 bg-gray-10">
              <div className="text-sm text-gray-500">
                {t('intermediaryGeneral.stats.distributorsCount')}
              </div>
              <div className="text-2xl font-semibold">{childBrokerageFirmsCount}</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
